/**
 * HaloLandingPage — public `/halo` destination for anyone who
 * clicked through from a shared halo snapshot PNG.
 *
 * Goals:
 *   - No auth, no signup required to view.
 *   - Shows the same visual language as the shared image so the
 *     story feels continuous ("oh — this is what I saw").
 *   - Single forward CTA, "Get your own halo →", funnelling to the
 *     existing invite-gated signup path.
 *   - UTM-aware — if the incoming URL carries a `utm_source` or
 *     `utm_campaign`, we thread it through to the CTA so the
 *     attribution survives even after the user picks up an invite
 *     and completes registration. No tracking pixels, no
 *     third-party JS — query-string only.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { generateHaloSnapshot } from '../utils/haloSnapshot';

// Given a cadence in days, produce a deterministic, visually-
// plausible list of 6 rotation timestamps stretching back from
// now. Pure → cheap to re-run on every slider tick.
const _timestampsFor = (cadenceDays) => {
  const now = Date.now();
  const day = 86400_000;
  return Array.from({ length: 6 }, (_, i) =>
    now - (6 - i) * cadenceDays * day
  );
};

const _forwardedQuery = (search) => {
  // Only forward marketing-adjacent params. Anything else (tokens,
  // internal state) is dropped so we don't accidentally carry
  // sensitive fragments across the signup boundary.
  const allow = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'ref'];
  const p = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const k of allow) {
    const v = p.get(k);
    if (v) out.set(k, v);
  }
  const s = out.toString();
  return s ? `?${s}` : '';
};

export const HaloLandingPage = () => {
  const location = useLocation();
  const [imgUrl, setImgUrl] = useState(null);
  const [cadenceDays, setCadenceDays] = useState(7);
  const lastUrlRef = useRef(null);

  // OpenGraph / Twitter card meta tags — injected via effect so
  // JS-capable social scrapers (Slack, Discord, modern Twitter)
  // still get a usable unfurl. True SSR-rendered OG (for cases
  // like Meta's static scraper) is tracked in the deferred list.
  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/halo`;
    const image = `${origin}/halo-og.png`; // fallback asset; static PNG
    const tags = {
      'og:type': 'website',
      'og:title': 'My security halo — Government Shield',
      'og:description': 'Keys I rotate. Contacts I nudge. Cadence I hold.',
      'og:url': url,
      'og:image': image,
      'twitter:card': 'summary_large_image',
      'twitter:title': 'My security halo — Government Shield',
      'twitter:description': 'Keys I rotate. Contacts I nudge. Cadence I hold.',
      'twitter:image': image,
    };
    const created = [];
    for (const [key, val] of Object.entries(tags)) {
      const attr = key.startsWith('og:') ? 'property' : 'name';
      // Update-or-create so repeated visits don't stack duplicates.
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', val);
    }
    const prevTitle = document.title;
    document.title = 'My security halo — Government Shield';
    return () => {
      for (const el of created) el.remove();
      document.title = prevTitle;
    };
  }, []);

  // Aspirational counter values — tuned so the image looks
  // earnable, not gamified. The slider only drives the sparkline.
  // `sasVerifiedByPeers` is the 4th pillar (voice SAS attestations
  // from distinct peers in the last 90d). Three is a reasonable
  // aspirational number that feels attainable without looking
  // hollow.
  const sampleCounters = useMemo(
    () => ({ sent: 7, worked: 4, pending: 1, sasVerifiedByPeers: 3 }),
    []
  );

  // Debounced blob re-render. Each slider drag fires a cascade of
  // state changes; debouncing at 180ms keeps the canvas renderer
  // from thrashing on slow devices while still feeling live.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const blob = await generateHaloSnapshot({
          ...sampleCounters,
          rotationTimestamps: _timestampsFor(cadenceDays),
        });
        if (!blob || cancelled) return;
        const url = URL.createObjectURL(blob);
        const prev = lastUrlRef.current;
        lastUrlRef.current = url;
        setImgUrl(url);
        // Revoke the previous blob URL after the new image has
        // started rendering so we never flash empty alt text.
        if (prev) setTimeout(() => URL.revokeObjectURL(prev), 250);
      } catch {
        /* Silent — the preview is decorative; the CTA still works. */
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cadenceDays, sampleCounters]);

  // Final cleanup on unmount so the last generated URL doesn't
  // leak after the user navigates away.
  useEffect(() => {
    return () => {
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
    };
  }, []);

  const signupHref = `/register${_forwardedQuery(location.search)}`;
  const loginHref = `/login${_forwardedQuery(location.search)}`;
  // Warmer return-visitor UX: if the browser already holds a
  // Government Shield keypair in localStorage, this device almost
  // certainly has an account — swap the primary CTA so the user
  // lands directly in their own dashboard instead of being asked
  // to sign up again. Checked once post-mount (not at module
  // scope) so SSR / prerender paths don't crash on missing
  // `window`, and we avoid a hydration-mismatch flash.
  const [hasLocalIdentity, setHasLocalIdentity] = useState(false);
  useEffect(() => {
    try {
      setHasLocalIdentity(!!localStorage.getItem('ghostcomm_keys'));
    } catch {
      /* storage disabled — fall back to signup CTA */
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span className="font-semibold">Government Shield</span>
          </Link>
          <Link
            to={loginHref}
            className="text-xs text-slate-400 hover:text-slate-200"
            data-testid="halo-landing-signin"
          >
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 space-y-14">
        <section className="text-center space-y-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-primary/80">
            <Sparkles className="w-4 h-4" /> The security halo
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Keys I rotate.<br className="sm:hidden" />{' '}
            <span className="text-slate-300">Contacts I nudge.</span><br className="sm:hidden" />{' '}
            <span className="text-primary">Cadence I hold.</span>
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-base leading-relaxed">
            Every Government Shield account keeps a visible rhythm of
            rotating its cryptographic keys. The halo is the shape of
            that rhythm — and of the contacts you've trained to keep
            theirs. Someone shared theirs with you. Now earn your own.
          </p>
        </section>

        <section
          className="rounded-xl border border-border/60 bg-card/40 p-4 sm:p-6 flex flex-col items-center gap-4"
          data-testid="halo-landing-preview"
        >
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="Example Government Shield security halo snapshot"
              className="max-w-full h-auto rounded-lg shadow-xl ring-1 ring-white/5"
              data-testid="halo-landing-preview-img"
            />
          ) : (
            <div className="text-sm text-slate-500 py-20">
              Rendering example halo…
            </div>
          )}

          {/* Interactive cadence slider — makes the static image
              a toy so visitors internalise "faster rotation ⇒
              tighter rhythm" without reading a paragraph. */}
          <div
            className="w-full max-w-xl space-y-2 pt-2"
            data-testid="halo-landing-cadence"
          >
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-slate-400 uppercase tracking-wider">
                Imagine rotating every
              </span>
              <span className="font-mono text-primary">
                {cadenceDays} {cadenceDays === 1 ? 'day' : 'days'}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              step={1}
              value={cadenceDays}
              onChange={(e) => setCadenceDays(Number(e.target.value))}
              className="w-full accent-primary cursor-pointer"
              aria-label="Rotation cadence in days"
              data-testid="halo-landing-cadence-slider"
            />
            <div className="flex items-center justify-between text-[10px] text-slate-500">
              <span>tighter (1d)</span>
              <span>default (7d)</span>
              <span>slack (30d)</span>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border/60 bg-card/40 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Rotate
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Your RSA + Kyber-768 keypairs refresh automatically every
              7 days, signed by a stable Dilithium identity. Old messages
              stay decryptable locally; old keys don't.
            </p>
          </div>
          <div className="rounded-lg border border-border/60 bg-card/40 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Nudge
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Contacts whose keys have stalled get a soft ring; one tap
              sends them an encrypted reminder to rotate. Healthy
              threads in, noisy drift out.
            </p>
          </div>
          <div className="rounded-lg border border-border/60 bg-card/40 p-5">
            {/* Violet accent subtly ties back to the VOICE VERIFIED
                tile in the snapshot above — same palette, same story. */}
            <div className="text-xs uppercase tracking-wider text-violet-300 mb-1">
              Verify
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Voice calls end with a six-digit code on both screens,
              derived from the DTLS handshake. Read it aloud — if it
              doesn't match, the line is tapped. Bakes MITM detection
              into a ten-second ritual.
            </p>
          </div>
          <div className="rounded-lg border border-border/60 bg-card/40 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Hold
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Your halo image is zero-PII — counters + rhythm, no names,
              no handles. Built to share the same way runners share a
              Strava screenshot, without giving up a single contact.
            </p>
          </div>
        </section>

        <section className="text-center space-y-4 pt-4">
          <Link
            to={hasLocalIdentity ? '/app' : signupHref}
            data-testid="halo-landing-cta"
            data-cta-variant={hasLocalIdentity ? 'returning' : 'new'}
          >
            <Button
              size="lg"
              className="text-base px-8 py-6 bg-primary hover:bg-primary/90"
            >
              {hasLocalIdentity ? 'Open your halo' : 'Get your own halo'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-xs text-slate-500">
            {hasLocalIdentity
              ? 'Welcome back — your rotation history and trust ledger are waiting.'
              : "Invite-only. You'll need a referral code from someone who's already rotating."}
          </p>
        </section>

        <section className="pt-6 border-t border-border/60 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            Auditable by design. See also:{' '}
            <Link to="/privacy/sealed-sender-demo" className="text-slate-300 hover:text-white underline">
              sealed sender demo
            </Link>
            {' '}·{' '}
            <Link to="/manifesto" className="text-slate-300 hover:text-white underline">
              manifesto
            </Link>
          </div>
          <div>
            <span className="text-slate-600">No tracking on this page.</span>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HaloLandingPage;
